import {
  DEFAULT_PIPE_TOOL_MODE,
  PIPE_TUBE_COLOR,
  pipeDetectProgressAtom,
  pipeDraftAtom,
  pipeToolModeAtom,
} from '@/atoms/pipe.atom';
import { meshPickTargetsAtom, pointCloudTargetsAtom } from '@/atoms/viewer.atom';
import { detectPipe, PipeDetectAbortError, PipeFitError } from '@/lib/pipe/detectPipe';
import type { Vec3 } from '@/lib/pipe/types';
import { useThree } from '@react-three/fiber';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import * as THREE from 'three';
import { CrosshairMarker } from './CrosshairMarker';
import { pickSurface } from './pickSurface';

/**
 * In-viewport pipe detector.
 *
 *  - **Seeking**: hover shows a crosshair; a click on a point-cloud surface
 *    seeds detection. We lock `sourceLayerId` to the clicked PCO.
 *  - **Detecting**: `detectPipe` walks the octree (camera-independent) — the
 *    panel shows progress; Escape/cancel aborts.
 *  - **Review**: a translucent tube previews the fit; two screen-space-sized
 *    handles let the user drag the endpoints along the axis. The panel edits
 *    radius. Both write back to `pipeDraftAtom`.
 *
 * Mirrors `StockpileTool`'s pick/cursor patterns. Detection is triggered by a
 * phase→'detecting' transition (effect below) so the panel can re-trigger it.
 */

const PIPE_COLOR = PIPE_TUBE_COLOR;

const toVec3 = (v: THREE.Vector3): Vec3 => [v.x, v.y, v.z];
const v3 = (a: Vec3) => new THREE.Vector3(a[0], a[1], a[2]);

export const PipeTool = ({ projectId }: { projectId: string }) => {
  const [mode, setMode] = useAtom(pipeToolModeAtom);
  const [draft, setDraft] = useAtom(pipeDraftAtom);
  const setProgress = useSetAtom(pipeDetectProgressAtom);
  const pcoTargets = useAtomValue(pointCloudTargetsAtom);
  const meshTargets = useAtomValue(meshPickTargetsAtom);
  const camera = useThree((s) => s.camera);
  const renderer = useThree((s) => s.gl);
  // Default controls (if registered via makeDefault) — disabled during a
  // handle drag so dragging an endpoint doesn't also orbit the camera.
  const controls = useThree((s) => s.controls) as { enabled?: boolean } | null;
  const raycaster = useMemo(() => new THREE.Raycaster(), []);
  const { t } = useTranslation('project');

  const [hoverPoint, setHoverPoint] = useState<THREE.Vector3 | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const detectingGuard = useRef(false);

  // Reset all pipe state when the project changes — the atoms are global, so a
  // stale detect panel / tube preview would otherwise carry over to the new
  // project. (The panel's `open` derives from this mode atom, so it closes too.)
  useEffect(() => {
    abortRef.current?.abort();
    setMode({ ...DEFAULT_PIPE_TOOL_MODE });
    setDraft(null);
    setProgress(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const enabled = mode.enabled;
  const isSeeking = enabled && (mode.phase === 'idle' || mode.phase === 'seeking');

  // Reset cursor / hover when disabled.
  useEffect(() => {
    if (!enabled) {
      renderer.domElement.style.cursor = 'default';
      setHoverPoint(null);
    }
  }, [enabled, renderer]);

  // Hover preview while seeking.
  useEffect(() => {
    if (!isSeeking) return;
    const dom = renderer.domElement;
    const onMove = (e: MouseEvent) => {
      const rect = dom.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      const y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(new THREE.Vector2(x, y), camera);
      const hit = pickSurface(renderer, camera, raycaster, pcoTargets, meshTargets);
      if (hit) {
        setHoverPoint(hit.position.clone());
        dom.style.cursor = 'crosshair';
      } else {
        setHoverPoint(null);
        dom.style.cursor = 'default';
      }
    };
    dom.addEventListener('pointermove', onMove);
    return () => {
      dom.removeEventListener('pointermove', onMove);
      dom.style.cursor = 'default';
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSeeking, pcoTargets, meshTargets, camera, renderer, raycaster]);

  // Click to seed detection.
  const downPos = useRef<{ x: number; y: number } | null>(null);
  useEffect(() => {
    if (!isSeeking) return;
    const dom = renderer.domElement;
    const DRAG = 5;
    const onDown = (e: MouseEvent) => {
      downPos.current = { x: e.clientX, y: e.clientY };
    };
    const onUp = (e: MouseEvent) => {
      if (e.button !== 0 || !downPos.current) return;
      const dx = e.clientX - downPos.current.x;
      const dy = e.clientY - downPos.current.y;
      downPos.current = null;
      if (Math.sqrt(dx * dx + dy * dy) > DRAG) return;

      const rect = dom.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      const y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(new THREE.Vector2(x, y), camera);
      const hit = pickSurface(renderer, camera, raycaster, pcoTargets, meshTargets);
      // pickSurface resolves the source PCO's layerId for us; a null layerId
      // means the closest hit was a mesh, not a point cloud — reject it.
      if (!hit || !hit.layerId) {
        toast.error(t('pipe.noPointCloudHit'));
        return;
      }
      setHoverPoint(null);
      setDraft(null);
      setMode({
        ...mode,
        phase: 'detecting',
        seed: toVec3(hit.position),
        sourceLayerId: hit.layerId,
        editFeatureId: null,
      });
    };
    dom.addEventListener('pointerdown', onDown);
    dom.addEventListener('pointerup', onUp);
    return () => {
      dom.removeEventListener('pointerdown', onDown);
      dom.removeEventListener('pointerup', onUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSeeking, mode, pcoTargets, meshTargets, camera, renderer, raycaster, t]);

  // Run detection on phase→'detecting'. Guarded so re-renders don't re-run.
  useEffect(() => {
    if (!enabled || mode.phase !== 'detecting' || !mode.seed || !mode.sourceLayerId) return;
    if (detectingGuard.current) return;
    const sourcePco = pcoTargets.find((p) => p.userData?.layerId === mode.sourceLayerId);
    if (!sourcePco) {
      toast.error(t('pipe.errorNoSourcePco'));
      setMode({ ...DEFAULT_PIPE_TOOL_MODE, enabled: true, phase: 'seeking' });
      return;
    }

    detectingGuard.current = true;
    const ac = new AbortController();
    abortRef.current = ac;
    setProgress({ phase: 'seed', done: 0, total: 1 });

    detectPipe(sourcePco, mode.seed, {
      signal: ac.signal,
      onProgress: (phase, done, total) => setProgress({ phase, done, total }),
    })
      .then((res) => {
        setDraft({
          vertices: [res.pStart, res.pEnd],
          radius: res.radius,
          axis: res.axis,
          arcCoverageDeg: res.arcCoverageDeg,
          rmsM: res.rmsM,
          inlierCount: res.inlierCount,
          confidence: res.confidence,
          edited: false,
        });
        setProgress(null);
        setMode((m) => ({ ...m, phase: 'review' }));
      })
      .catch((err) => {
        setProgress(null);
        if (err instanceof PipeDetectAbortError) {
          toast.info(t('pipe.detectCanceled'));
        } else if (err instanceof PipeFitError) {
          toast.error(err.message);
        } else {
          toast.error(err?.message ?? t('pipe.detectFailed'));
        }
        setMode((m) => ({ ...m, phase: 'seeking', seed: null }));
      })
      .finally(() => {
        detectingGuard.current = false;
        abortRef.current = null;
      });

    return () => {
      ac.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, mode.phase, mode.seed, mode.sourceLayerId]);

  // Escape cancels everything.
  useEffect(() => {
    if (!enabled) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      abortRef.current?.abort();
      setDraft(null);
      setProgress(null);
      setMode({ ...DEFAULT_PIPE_TOOL_MODE });
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  // ── Endpoint dragging (review). Moves an endpoint along the axis by finding
  // the closest point on the axis line to the mouse ray. ───────────────────
  const dragEnd = useRef<'start' | 'end' | null>(null);
  useEffect(() => {
    if (mode.phase !== 'review' || !draft) return;
    const dom = renderer.domElement;
    const onMove = (e: MouseEvent) => {
      if (!dragEnd.current || draft.vertices.length < 2) return;
      const rect = dom.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      const y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(new THREE.Vector2(x, y), camera);
      const O = raycaster.ray.origin;
      const D = raycaster.ray.direction; // normalised
      // The dragged terminal moves along the line through its neighbour vertex
      // in the direction of its own terminal segment.
      const verts = draft.vertices;
      const isStart = dragEnd.current === 'start';
      const vi = isStart ? 0 : verts.length - 1;
      const neighbor = isStart ? verts[1] : verts[verts.length - 2];
      const C = v3(neighbor);
      const A = new THREE.Vector3(verts[vi][0] - neighbor[0], verts[vi][1] - neighbor[1], verts[vi][2] - neighbor[2]);
      if (A.lengthSq() < 1e-12) return;
      A.normalize();
      // Closest point on the axis line to the mouse ray: solve for sc.
      const w0 = new THREE.Vector3().subVectors(C, O);
      const b = A.dot(D);
      const dCoef = A.dot(w0);
      const eCoef = D.dot(w0);
      const denom = 1 - b * b;
      if (Math.abs(denom) < 1e-6) return; // ray ∥ axis
      const sc = (b * eCoef - dCoef) / denom;
      const np = new THREE.Vector3().copy(A).multiplyScalar(sc).add(C);
      setDraft((prev) => {
        if (!prev) return prev;
        const nv = [...prev.vertices];
        nv[vi] = toVec3(np);
        return { ...prev, vertices: nv, edited: true };
      });
    };
    const onUp = () => {
      if (dragEnd.current && controls) controls.enabled = true;
      dragEnd.current = null;
    };
    dom.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    return () => {
      dom.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode.phase, draft, camera, renderer, raycaster]);

  if (!enabled) return null;

  const verts = mode.phase === 'review' && draft ? draft.vertices : null;

  const startDrag = (which: 'start' | 'end') => (e: any) => {
    e?.stopPropagation?.();
    dragEnd.current = which;
    if (controls) controls.enabled = false;
  };

  return (
    <>
      {/* Hover crosshair while seeking (fresh pick OR chain "continue" pick). */}
      {isSeeking && hoverPoint && (
        <CrosshairMarker position={hoverPoint} color={PIPE_COLOR} screenSize={0.014} />
      )}

      {/* Tube preview (one cylinder per polyline segment) + handles. */}
      {verts && verts.length >= 2 && (
        <>
          {verts.slice(0, -1).map((a, i) => {
            const A = v3(a);
            const B = v3(verts[i + 1]);
            const m = new THREE.Vector3().addVectors(A, B).multiplyScalar(0.5);
            return (
              <group key={i} position={[m.x, m.y, m.z]}>
                <PipeMesh start={A} end={B} radius={draft!.radius} />
              </group>
            );
          })}
          {/* Interior joints — small non-draggable markers. */}
          {verts.slice(1, -1).map((p, i) => (
            <CrosshairMarker key={`joint-${i}`} position={p} color={PIPE_COLOR} screenSize={0.012} />
          ))}
          {/* Terminal handles — draggable along their segment. */}
          <CrosshairMarker
            position={verts[0]}
            color={PIPE_COLOR}
            screenSize={0.02}
            onPointerDown={startDrag('start')}
            cursor='grab'
          />
          <CrosshairMarker
            position={verts[verts.length - 1]}
            color={PIPE_COLOR}
            screenSize={0.02}
            onPointerDown={startDrag('end')}
            cursor='grab'
          />
        </>
      )}
    </>
  );
};

/** Translucent cylinder oriented from `start`→`end`. Coords are group-local. */
const PipeMesh = ({
  start,
  end,
  radius,
}: {
  start: THREE.Vector3;
  end: THREE.Vector3;
  radius: number;
}) => {
  const dir = useMemo(() => new THREE.Vector3().subVectors(end, start), [start, end]);
  const length = dir.length();
  // Orientation as an Euler tuple (not a Quaternion instance) — passing math
  // class instances to transform props can hit three's read-only position/
  // quaternion descriptors in some bundles; arrays go through `.set()` safely.
  const rotation = useMemo<[number, number, number]>(() => {
    if (length <= 1e-9) return [0, 0, 0];
    const q = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir.clone().normalize());
    const e = new THREE.Euler().setFromQuaternion(q, 'XYZ');
    return [e.x, e.y, e.z];
  }, [dir, length]);

  if (length < 1e-6 || radius <= 0) return null;
  return (
    <mesh position={[0, 0, 0]} rotation={rotation} renderOrder={Number.MAX_SAFE_INTEGER - 1}>
      {/* radial segments 28 for a smooth tube; open-ended so we see through it */}
      <cylinderGeometry args={[radius, radius, length, 28, 1, true]} />
      <meshBasicMaterial color='#22d3ee' transparent opacity={0.35} depthTest={false} side={THREE.DoubleSide} />
    </mesh>
  );
};
